import { Component, Input, OnInit } from '@angular/core';

import { Observable, of, Subscription } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

import { AbstractDialogComponent } from '../../../../Core/Dialogs/components/AbstractDialog/abstract-dialog.component';
import { FullProfileAnimationModel } from '../../../../Core/Dialogs/models/full-profile-animation.model';
import { CoreDialogService } from '../../../../Core/Dialogs/services/dialog.service';
import { FilterService } from '../../services/abstract-filter.service';
import { DropdownOptionModel } from '../../../Forms/models/dropdown-option.model';
import { ButtonTypesEnum } from '../../../Forms/enums/button-types.enum';
import { FilterTypeModel } from '../../models/abstract-filter-type.model';
import { ChipModel } from '../../../Forms/models/chip.model';
import { IncludeExcludeEnum } from '../../enums/include-exclude.enum';
import { SavedFilterService } from '../../services/saved-filter.service';
import { SavedFilter } from '../../models/saved-filter.model';
import { ResourceActionTypes } from '../../../Basic/enums/resource-actions.enum';

enum SaveFilterPanelStateEnum {
    new,
    update
}

@Component({
    selector: 'agpr-save-filter-dialog',
    templateUrl: './save-filter-dialog.component.html',
    styleUrls: ['./save-filter-dialog.component.scss'],
    standalone: false
})
export class SaveFilterDialogComponent extends AbstractDialogComponent implements OnInit {

    @Input() id: string = 'saveFilter';
    @Input() overlayContainer: Element;
    @Input() animationTrigger: Observable<FullProfileAnimationModel>;

    errors: { [key: string]: boolean } = {
        duplicate: false,
        empty: false,
        noFilterSelected: false,
        incompatibleRegionalFilter: false,
        invalidLength: false
    };
    saveFilterPanelStateEnum = SaveFilterPanelStateEnum;
    saveType: SaveFilterPanelStateEnum = SaveFilterPanelStateEnum.new;
    newFilterName: string = '';
    showErrors: boolean = false;
    filterOptions: DropdownOptionModel[] = [];
    selectedFilterOption: DropdownOptionModel;
    flatFilters: FilterTypeModel[];
    filterToSave: SavedFilter;

    private subscriptions: Subscription[] = [];

    readonly buttonTypes = ButtonTypesEnum;
    readonly includeExclude = IncludeExcludeEnum;

    constructor(
        public coreDialogService: CoreDialogService,
        private savedFilterService: SavedFilterService,
        private filterService: FilterService,
    ) {
        super(coreDialogService, 'SaveFilterPanel');
    }

    ngOnInit() {
        this.subscriptions.push(
            this.savedFilterService.all$.subscribe(
                filters => {
                    this.filterOptions = filters?.map(x => ({label: x.name, value: x})) || [];
                }
            ),
            this.filterService.getFlatFilters().subscribe(
                (flatFilters) => {
                    this.flatFilters = flatFilters;
                }
            )
        );
    }

    save() {
        this.filterToSave = this.filterService.prepareFilterModelToSave();

        if (!this.isValid()) {
            return;
        }

        if (this.saveType === SaveFilterPanelStateEnum.new) {
            this.filterToSave.name = this.newFilterName;

            this.savedFilterService.createResource(this.filterToSave).pipe(
                switchMap((res: any) => this.applySavedValueAndRefresh(res))
            ).subscribe((res: any) => {
                // @TODO - not a good way of handle errors
                if (!res?.error) {
                    this.closeDialog();
                }
            });
        } else if (this.saveType === SaveFilterPanelStateEnum.update) {
            this.savedFilterService.getById(this.selectedFilterOption.value.id).pipe(
                switchMap(selectedFilter => {
                    selectedFilter.query = this.filterToSave.query;
                    selectedFilter.showSimilar = this.filterToSave.showSimilar;
                    this.filterToSave = selectedFilter;

                    return this.savedFilterService.updateResource(this.filterToSave, { actionType: ResourceActionTypes.UPDATE });
                }),
                switchMap((res: any) => this.applySavedValueAndRefresh(res))
            ).subscribe((res: any) => {
                // @TODO - not a good way of handle errors
                if (!res?.error) {
                    this.closeDialog();
                }
            });
        }
    }

    /**
     * Removes a chip from a specified filter
     */
    removeChip(filter: FilterTypeModel, chip: ChipModel): void {
        filter.removeCurrentChip(chip);
    }

    /**
     * Once a create/update call has succeeded, sets the current value on the same filterToSave
     * reference before refreshing the saved filters list, so the saved filter checkbox list
     * picks up the correct checked state as soon as it re-renders.
     */
    private applySavedValueAndRefresh(res: any): Observable<any> {
        if (res?.error) {
            return of(res);
        }

        this.filterService.clearFilters();
        this.filterService.savedFilters.setCurrentValue([this.filterToSave]);
        this.filterService.savedFilters.filterCards = [];
        this.filterService.savedFilters.filterCards.push(
            this.filterService.constructFilterCardFromValue(this.filterToSave)
        );

        return this.savedFilterService.refreshAllManual().pipe(map(() => res));
    }

    clearErrors(): void {
        this.showErrors = false;
        this.errors.duplicate = false;
        this.errors.empty = false;
        this.errors.noFilterSelected = false;
        this.errors.incompatibleRegionalFilter = false;
        this.errors.invalidLength = false;
    }

    isValid(): boolean {
        if (this.filterToSave.query.includedRegions?.length > 0 && this.filterToSave.query.excludedRegions?.length > 0) {
            this.showErrors = true;
            this.errors.incompatibleRegionalFilter = true;
            return false;
        }

        if (this.saveType === SaveFilterPanelStateEnum.new) {
            if (this.newFilterName.trim().length > 0) {
                if (!this.inputIsValid(this.newFilterName.trim())) {
                    this.showErrors = true;
                    this.errors.invalidLength = true;
                    return false;
                }
                // Check if duplicates exist
                const isDuplicated = !!this.filterOptions.find(x => x.label.toLocaleLowerCase() === this.newFilterName.trim().toLocaleLowerCase());
                if (isDuplicated === true) {
                    this.showErrors = true;
                    this.errors.duplicate = true;
                }
                return !isDuplicated;
            } else {
                // Check if empty
                this.showErrors = true;
                this.errors.empty = true;
                return false;
            }
        } else if (this.saveType === SaveFilterPanelStateEnum.update) {
            if (!this.selectedFilterOption) {
                this.showErrors = true;
                this.errors.noFilterSelected = true;
            }
            return !!this.selectedFilterOption;
        }
    }

    inputIsValid(input: string): boolean{
        return this.savedFilterService.getNameValidator().isValid(input);
    }
}
